import { z } from "zod";
import { ProjectChoice } from "./ongoing.js";
import { Estimate } from "./estimate.js";
import { LineInput, SheetInput } from "./estimate.js";
import { BillingMode } from "./closing.js";
import { UNISSUED_MSG } from "./unissued.js";

// ---------------------------------------------------------------------------
// 納品書（営業力 E2 の 1a → 現場力にも開く。#266、docs/96 v0.2、DB 20261028_1800）
//   documents の doc_type='delivery'（005 からある種類）。名前は会社の言葉 {deliveryNote}（既定 納品書）。両製品・門はモジュール sales、
//   画面は使う機能 deliveryNote（既定 営業力オン・現場力オフ）で出し分ける（API は止めない）。
//   起こし方は3つ：受注した見積から（明細と数量を選ぶ）／{project}から（見積なし）／{project}なし（裏で自動の{project}）。
//   分納の残＝見積の行ごとに 見積の数量 − 発行した納品書の数量（納品書の行の系譜は見積の行の系譜のまま。DB の v_order_delivery_state）。
//   税を確定する書類（{customer}ごと＝payment_terms.qualified_invoice_unit）：請求書（既定）なら納品書に税額を書かない（「消費税は請求書にて」）、
//   納品書なら納品書ごとに税率ごとに1回丸め、請求書は足すだけ（ルール1）。束ね請求は同じ受注（見積なしは同じ{project}）の納品書だけ（ほかは E3 の締め）。
//   ※ この製品で「送付」（送付状・メール）は delivery／document_deliveries という名前を使っている。納品書の識別子は deliveryNote・/delivery-notes
// ---------------------------------------------------------------------------

/** 税を確定する書類（{customer}ごと。DB の qualified_invoice_unit） */
export const TaxOn = z.enum(["invoice", "delivery"]);
export type TaxOn = z.infer<typeof TaxOn>;
/** 画面の選択肢（{deliveryNote} は画面が t() で埋める） */
export const TAX_ON_LABEL: Record<TaxOn, string> = { invoice: "請求書", delivery: "{deliveryNote}" };

/** 束ね請求の形：全行＝納品書ごとに見出し行「10/03 納品 D-0001」＋その明細／1枚1行＝納品書1枚を1行（税率が2つなら税率ごとに1行） */
export const BundleMode = z.enum(["lines", "perNote"]);
export type BundleMode = z.infer<typeof BundleMode>;
export const BUNDLE_MODE_LABEL: Record<BundleMode, string> = { lines: "全行", perNote: "1枚1行" };

/**
 * 帳票の種類（#266 1b、docs/96 §14）：納品書／検収書。検収書は納品書の双子＝documents の doc_type 'acceptance'・番号は会社の規則（既定 AC-0001）・
 * 画面は同じエディタ（{acceptance}・検収日・検収者・元の{deliveryNote}）。API は /delivery-notes と /acceptances（同じ口の形）
 */
export const NoteDocKind = z.enum(["deliveryNote", "acceptance"]);
export type NoteDocKind = z.infer<typeof NoteDocKind>;
export const NOTE_PATH: Record<NoteDocKind, string> = { deliveryNote: "/delivery-notes", acceptance: "/acceptances" };

/** 受領・検収の記録（最後の1つ。電子＝受領・検収のページの「受領」「検収」／書面＝日付の窓） */
export const AcceptanceRecord = z.object({
  kind: z.enum(["receive", "accept"]),
  method: z.enum(["electronic", "paper"]),
  /** 受領日・検収日（YYYY-MM-DD） */
  on: z.string(),
  /** 記録した日時 */
  at: z.string(),
  /** 電子の記録番号（書面は null） */
  recordNo: z.string().nullable(),
  /** 検収者（{customer}の人の氏名。無ければ空） */
  by: z.string(),
});
export type AcceptanceRecord = z.infer<typeof AcceptanceRecord>;

/** 納品書：見積の形（行・内訳・計算の見え方は見積のエディタ）に、納品書だけの欄を足したもの（検収書も同じ形＝kind） */
export const DeliveryNote = Estimate.extend({
  kind: NoteDocKind.default("deliveryNote"),
  revision: Estimate.shape.revision.extend({
    /** 受領日（{customer}が品物を受け取った日。社内の記録＝紙に出さない。1b から受領の記録で入る） */
    receivedOn: z.string().nullable(),
    /** 検収日・検収者（検収書だけ。検収の記録で入る） */
    acceptedOn: z.string().nullable().default(null),
    acceptedBy: z.string().default(""),
  }),
  /** 帳票の名前（下書き＝今の会社の言葉・出したあと＝固めた名前） */
  names: z.object({ deliveryNote: z.string(), acceptance: z.string().default("") }),
  /**
   * 請求の形（#267、docs/97）：mode＝今効いている値（伝票の上書き → 発行時に固めた値 → {project} → 工事 → {customer} → 都度）、
   * source＝どこで決まったか（document＝この1枚・frozen＝発行時に固めた値・group・project・partner・system_default）、
   * closingClient＝この1枚の上書きを除いた値（{project} → 工事 → {customer}）が締めか（発行の確認の「締めに入れない」を出すか）
   */
  billing: z
    .object({ mode: BillingMode, source: z.enum(["document", "frozen", "group", "project", "partner", "system_default"]), closingClient: z.boolean() })
    .nullable()
    .default(null),
  /** 受領・検収の記録（最後の1つ。まだ無ければ null） */
  record: AcceptanceRecord.nullable().default(null),
  /** 受領・検収のページ（生きている鍵。まだ作っていなければ null） */
  ackLink: z.object({ url: z.string(), expiresAt: z.string(), opened: z.boolean() }).nullable().default(null),
  /** スイッチ「{acceptance}」（{deliveryNote}だけ。3段目＝この1枚・null は{customer}に従う／2段目＝{customer}／いまの値） */
  acceptanceSwitch: z.object({ attach: z.boolean().nullable(), partner: z.boolean().nullable(), on: z.boolean() }).nullable().default(null),
  /** 検収書の元の{deliveryNote}（検収書だけ） */
  sourceNote: z.object({ id: z.string().uuid(), displayNo: z.string().nullable() }).nullable().default(null),
  /** この{deliveryNote}から作った検収書（{deliveryNote}だけ。取消して下書きに戻したものを除く） */
  acceptances: z.array(z.object({ id: z.string().uuid(), displayNo: z.string().nullable(), status: z.string() })).default([]),
  /** 検査期限（業種 IT・システムの検収書だけ＝発行日から 14 日。表示だけ＝みなし検収は 2回目） */
  inspectUntil: z.string().nullable().default(null),
  /** 税を確定する書類（下書き＝{customer}の今の設定・発行したあと＝固めた値） */
  taxOn: TaxOn,
  /** 元の見積（受注）。見積なしの納品書は null */
  sourceQuote: z.object({ id: z.string().uuid(), displayNo: z.string().nullable(), subject: z.string() }).nullable(),
  /** 載った請求書（下書きも）。載っていなければ null */
  invoice: z.object({ id: z.string().uuid(), documentNo: z.string().nullable(), status: z.string() }).nullable(),
  /** 請求書作成を出すか（発行済・未請求・分割の受注でない） */
  canBill: z.boolean(),
  /** 取消しの断り（請求書に載っている） */
  unissueBlockedBy: z.enum(["billed", "record"]).nullable(),
});
export type DeliveryNote = z.infer<typeof DeliveryNote>;

/** 受注した見積の行ごとの納品の様子（GET /estimates/:id/delivery-state。DB の v_order_delivery_state） */
export const DeliveryStateLine = z.object({
  /** 見積の行（今の回）の id と系譜 */
  lineId: z.string().uuid(),
  lineageId: z.string().uuid(),
  no: z.number().int(),
  role: z.string(),
  name: z.string(),
  unit: z.string(),
  /** 見積の数量（明細の行だけ） */
  quantity: z.number().nullable(),
  /** 発行した納品書に載った数量 */
  delivered: z.number(),
  /** 残（明細の行だけ） */
  remaining: z.number().nullable(),
  /** 載った納品書の枚数（率・値引き・別途の行は「済」の印に使う） */
  deliveredCount: z.number().int(),
  /** 単価（明細の行だけ。作成の窓の「今回」の金額＝今回の数量 × 単価） */
  unitPrice: z.number().nullable().default(null),
  /** 見積の行の金額（税抜。率・値引き・別途の行の今回の金額） */
  amount: z.number().int().nullable().default(null),
});
export type DeliveryStateLine = z.infer<typeof DeliveryStateLine>;
export const DeliveryState = z.object({
  quoteId: z.string().uuid(),
  /** 受注していなければ false（納品書を作れない） */
  ordered: z.boolean(),
  lines: z.array(DeliveryStateLine),
  /** 残のある明細の行の数 */
  remainingRows: z.number().int(),
  /** 残の金額（税抜。残 × 単価の和） */
  remainingAmount: z.number().int(),
  /** この見積から作った納品書（下書きも） */
  notes: z.array(z.object({ id: z.string().uuid(), documentNo: z.string().nullable(), status: z.string(), issueDate: z.string(), invoiceId: z.string().uuid().nullable().default(null) })),
  /** 見積から直接起こした請求書がある（納品書から請求しない）・分割請求の受注 */
  directInvoice: z.boolean(),
  installments: z.boolean(),
});
export type DeliveryState = z.infer<typeof DeliveryState>;

/** POST /delivery-notes：受注した見積から（行と今回の数量）／{project}から（見積なし）／{customer}だけ（{project}なし＝自動の{project}） */
export const DeliveryNoteCreate = z
  .object({
    fromQuoteId: z.string().uuid().optional(),
    /** 見積から：入れる行（見積の今の回の行の id）と今回の数量。省いた行は入れない。明細の行は 0 より大きい数量だけ */
    lines: z.array(z.object({ lineId: z.string().uuid(), quantity: z.number().min(0).max(99_999_999).nullable() })).max(1000).optional(),
    projectGroupId: z.string().uuid().optional(),
    partnerId: z.string().uuid().optional(),
    issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    siteAddress: z.string().max(200).optional(),
    subject: z.string().trim().max(200).optional(),
    /**
     * {customer}だけ（partnerId）で作るときの{project}の選び方（#289、docs/101 §6）：{project}の id／"new"＝新規（{subject}と同じ。今まで）／
     * "ongoing:new"＝継続案件を作る。省けば new。完了した継続案件に作ると答えに warning（止めない）
     */
    projectId: ProjectChoice.optional(),
  })
  .refine((v) => [v.fromQuoteId, v.projectGroupId, v.partnerId].filter(Boolean).length === 1, { message: "どこから作るかを1つだけ指定します" });
export type DeliveryNoteCreate = z.infer<typeof DeliveryNoteCreate>;

/** PUT /delivery-notes/:id（版つき。違えば 409 DELIVERY_NOTE_STALE）。省いた欄は変えない。行・内訳・頭は下書きだけ（受領日は発行のあとでも） */
export const DeliveryNoteSave = z.object({
  subject: z.string().max(200).optional(),
  /** 納品日（＝発行日） */
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** 納品先 */
  siteAddress: z.string().max(200).optional(),
  notes: z.string().max(2000).optional(),
  receivedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  /** スイッチ「{acceptance}」（{deliveryNote}だけ。null＝{customer}に従う。発行のあとでも変えられる） */
  attachAcceptance: z.boolean().nullable().optional(),
  sheets: z.array(SheetInput).max(50).optional(),
  lines: z.array(LineInput).max(1000).optional(),
  version: z.string().min(1).max(40).optional(),
});
export type DeliveryNoteSave = z.infer<typeof DeliveryNoteSave>;

export const DeliveryNoteIssueRequest = z.object({
  company: z.object({ name: z.string().trim().min(1).max(100) }).optional(),
  /** 「締めに入れない」（#267）：この1枚の請求の形を都度にしてから発行する（締めの{customer}・{project}のときだけ画面に出る） */
  separate: z.boolean().optional(),
  version: z.string().optional(),
});
export type DeliveryNoteIssueRequest = z.infer<typeof DeliveryNoteIssueRequest>;
export const DeliveryNoteIssueResult = z.object({ documentNo: z.string(), issuedAt: z.string(), updatedAt: z.string() });
export type DeliveryNoteIssueResult = z.infer<typeof DeliveryNoteIssueResult>;

/** 一覧のタブ：下書き／発行済（未請求）／請求済／すべて／{acceptance}（1b。使う機能「{acceptance}」の会社だけ画面に出す） */
export const DeliveryNoteTab = z.enum(["draft", "issued", "billed", "all", "acceptance"]);
export type DeliveryNoteTab = z.infer<typeof DeliveryNoteTab>;
export const DELIVERY_NOTE_TAB_LABEL: Record<DeliveryNoteTab, string> = { draft: "下書き", issued: "発行済", billed: "請求済", all: "すべて", acceptance: "{acceptance}" };

/** 一覧の1行 */
export const DeliveryNoteSummary = z.object({
  id: z.string().uuid(),
  documentNo: z.string().nullable(),
  status: z.string(),
  /** 帳票の種類（{acceptance}のタブの行は acceptance） */
  kind: NoteDocKind.default("deliveryNote"),
  /** 一覧の状態：draft 下書き／unissued 下書き（番号あり。#278）／issued 発行済（未請求・{acceptance}は未確認）／received 受領済（未請求）／billed 請求済／accepted 検収済 */
  state: z.enum(["draft", "unissued", "issued", "received", "billed", "accepted"]),
  clientId: z.string().uuid(),
  clientName: z.string(),
  group: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  /** 束ね請求の単位（同じ受注・見積なしは同じ{project}）。画面は同じ単位の行だけ一緒に選べる */
  billKey: z.string(),
  subject: z.string(),
  issueDate: z.string(),
  receivedOn: z.string().nullable(),
  /**
   * 税抜・税込。税込は版の合計（消費税を含む）で、税を確定する書類が請求書の{customer}の納品書でも版には消費税が入っている
   * （紙・発行前の確認・一覧は税抜だけを出し、消費税は「請求書にて」＝請求書が明細から税率ごとに1回計算し直す）
   */
  subtotal: z.number().int(),
  total: z.number().int(),
  invoice: z.object({ id: z.string().uuid(), documentNo: z.string().nullable() }).nullable(),
  updatedAt: z.string(),
});
export type DeliveryNoteSummary = z.infer<typeof DeliveryNoteSummary>;
export const DeliveryNoteList = z.object({
  items: z.array(DeliveryNoteSummary),
  counts: z.object({ draft: z.number().int(), issued: z.number().int(), billed: z.number().int(), all: z.number().int(), acceptance: z.number().int().default(0) }),
  /** 表示中の税抜の合計 */
  subtotal: z.number().int(),
});
export type DeliveryNoteList = z.infer<typeof DeliveryNoteList>;

/** POST /invoices {fromDeliveryNoteIds, bundleMode}：束ね請求（同じ{customer}・同じ受注（見積なしは同じ{project}）の発行済・未請求の納品書） */
export const InvoiceFromDeliveryNotes = z.object({
  fromDeliveryNoteIds: z.array(z.string().uuid()).min(1).max(50),
  bundleMode: BundleMode.default("lines"),
});
export type InvoiceFromDeliveryNotes = z.infer<typeof InvoiceFromDeliveryNotes>;

/** 納品書の状態の文字（一覧と見出しで同じ）。{acceptance}の発行済は「未確認」 */
export function deliveryNoteStateText(s: DeliveryNoteSummary["state"], kind: NoteDocKind = "deliveryNote"): string {
  if (s === "draft") return "下書き";
  if (s === "unissued") return UNISSUED_MSG.chip;
  if (s === "issued") return kind === "acceptance" ? "未確認" : "発行済";
  if (s === "received") return "受領済";
  if (s === "accepted") return "検収済";
  return "請求済";
}

/** POST /acceptances：{deliveryNote}から（明細を写す）／受注した見積から（全行）／請求書から（全行）／{project}から／{customer}だけ（自動の{project}） */
export const AcceptanceCreate = z
  .object({
    fromDeliveryNoteId: z.string().uuid().optional(),
    fromQuoteId: z.string().uuid().optional(),
    fromInvoiceId: z.string().uuid().optional(),
    projectGroupId: z.string().uuid().optional(),
    partnerId: z.string().uuid().optional(),
    issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    subject: z.string().trim().max(200).optional(),
  })
  .refine((v) => [v.fromDeliveryNoteId, v.fromQuoteId, v.fromInvoiceId, v.projectGroupId, v.partnerId].filter(Boolean).length === 1, { message: "どこから作るかを1つだけ指定します" });
export type AcceptanceCreate = z.infer<typeof AcceptanceCreate>;

/** POST /delivery-notes/:id/paper-record・/acceptances/:id/paper-record：書面で受領・書面で検収（日付の窓）。検収者は検収書だけ */
export const AcceptancePaperRecord = z.object({
  on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  by: z.string().trim().max(60).optional(),
  version: z.string().optional(),
});
export type AcceptancePaperRecord = z.infer<typeof AcceptancePaperRecord>;

/** POST /delivery-notes/:id/ack-link・/acceptances/:id/ack-link の答え（受領・検収のページの URL。生きている鍵があればそれ） */
export const AcceptanceLinkResult = z.object({ url: z.string(), expiresAt: z.string(), updatedAt: z.string() });
export type AcceptanceLinkResult = z.infer<typeof AcceptanceLinkResult>;

/** 受領・検収のページの鍵（/a/<鍵>。base64url 43字） */
export const ACCEPTANCE_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

/** 受領・検収のページ（ログインなし。GET /api/v1/a/:token） */
export const AcceptancePublicPage = z.object({
  company: z.string(),
  product: z.enum(["genba", "eigyo"]),
  /** receive＝{deliveryNote}の受領・accept＝{acceptance}の検収 */
  kind: z.enum(["receive", "accept"]),
  /** 帳票の名前（会社の言葉） */
  title: z.string(),
  docNo: z.string(),
  subject: z.string(),
  /** 税抜（紙の合計）・消費税（税を確定する書類が{deliveryNote}のときだけ） */
  subtotal: z.number().int(),
  taxTotal: z.number().int().nullable(),
  issueDate: z.string(),
  /** 明細の要約（見出し・明細・値引き・別途。名称・数量・単位・金額） */
  lines: z.array(z.object({ role: z.string(), name: z.string(), quantity: z.number().nullable(), unit: z.string(), amount: z.number().nullable() })),
  lineCount: z.number().int(),
  /** 送った会社でログイン中（確かめ用＝押せない） */
  sender: z.boolean(),
  /** 済んだ記録（電子・書面） */
  done: z.object({ at: z.string(), on: z.string(), recordNo: z.string().nullable(), method: z.enum(["electronic", "paper"]) }).nullable(),
});
export type AcceptancePublicPage = z.infer<typeof AcceptancePublicPage>;
export const AcceptanceConfirmResult = z.object({ at: z.string(), recordNo: z.string() });
export type AcceptanceConfirmResult = z.infer<typeof AcceptanceConfirmResult>;

/** 受領・検収のページの文（Figma ④。{word}＝受領／検収） */
export const ACCEPTANCE_PUBLIC_MSG = {
  from: "送信元：{company}",
  subtotal: "合計（税抜）",
  taxOnInvoice: "消費税は請求書にて",
  lines: "明細　{n}行",
  more: "ほか {n}行（PDF でご確認ください）",
  pdf: "PDF を保存",
  receive: "受領",
  accept: "検収",
  sending: "送信中…",
  sentence: "「{word}」を押すと、記載の内容を確認したことが日時とともに記録され、送信元に通知されます",
  doneChip: "{word}済 {md}",
  done: "{word}済（{at}）",
  recordNo: "記録番号 {no}",
  paperDone: "{word}済（書面・{on}）",
  sender: "送った会社の画面からは押せません（確かめ用の表示です）",
  notFound: "このページは見つかりません",
  gone: "このページの受付を終了しました",
  goneNote: "{company}にお問い合わせください。",
  already: "もう{word}済みです",
  tooMany: "しばらくしてから開いてください",
} as const;

/** 受領・検収の記録の文（ログインの画面） */
export const ACCEPTANCE_MSG = {
  paperReceive: "書面で受領",
  paperAccept: "書面で検収",
  receivedOn: "受領日",
  acceptedOn: "検収日",
  acceptedBy: "検収者",
  linkCopy: "リンクコピー",
  linkCopied: "{word}のページのリンクをコピーしました。",
  recorded: "{word}を記録しました。",
  create: "{acceptance}作成",
  createFromNote: "{acceptance}作成",
  switch: "{acceptance}",
  switchHelp: "この1枚に{acceptance}を付けるか（{acceptance}作成・請求書の PDF の組合せ）。既定は{customer}の設定です。",
  partnerLabel: "{acceptance}",
  partnerHelp: "この{customer}の{deliveryNote}・請求書に{acceptance}を付けるかの既定。会社に従う＝使う機能「{acceptance}」のとおり。",
  partnerFollow: "会社に従う",
  partnerOn: "オン",
  partnerOff: "オフ",
  partnerSaved: "{acceptance}の既定を変更しました。",
  unconfirmed: "未確認",
  received: "受領済",
  accepted: "検収済",
  inspectUntil: "検査期限",
  inspectHelp: "IT・システムの{acceptance}は、発行日から 14 日を検査期限として表示します（表示だけです）。",
  source: "元",
  noteHasAcceptance: "{acceptance} {no}",
  unissueHasRecord: "受領・検収の記録がある帳票は取り消せません",
  recordDone: "もう受領・検収の記録があります",
  futureDate: "今日より後の日付は入れられません",
  noteRequired: "発行した{deliveryNote}から作成できます",
  bundleNotes: "請求書＋{deliveryNote}",
  bundleAll: "請求書＋{deliveryNote}＋{acceptance}",
  bundleHelp: "請求書の PDF の後ろに、この請求書に載った{deliveryNote}（と、その{acceptance}）の PDF をつなげます。",
} as const;

/** 束ね請求の見出し行・1枚1行の名前（「10/03 納品 D-0001」）。日付は YYYY-MM-DD */
export function bundleLineName(date: string, no: string | null, word = "納品"): string {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(date);
  const md = m ? `${m[1]}/${m[2]}` : date;
  return `${md} ${word}${no ? ` ${no}` : ""}`;
}

/** 画面・API の文（{deliveryNote}・{customer}・{project} は会社の言葉＝画面は t()、API は fillLabels） */
export const DELIVERY_NOTE_MSG = {
  title: "{deliveryNote}",
  list: "{deliveryNote}",
  create: "{deliveryNote}作成",
  newTitle: "{deliveryNote}作成",
  notFound: "その{deliveryNote}は見つかりません",
  notOrdered: "受注した見積から{deliveryNote}を作成できます",
  overRemaining: "残より多い数量があります（{name}：残 {rest}）",
  noLines: "明細が1行もありません。行を追加してから発行してください",
  noRows: "{deliveryNote}に入れる行を選んでください",
  draftExists: "この見積の{deliveryNote}の下書きがあります",
  directInvoice: "この見積は請求書を見積から作成しています。{deliveryNote}から請求書は作成できません",
  installments: "分割請求の受注です。請求書は分割の予定から作成します",
  customerRequired: "{customer}を選んでください",
  stale: "他の利用者が変更しました。再読込しますか？",
  notDraft: "下書きの{deliveryNote}だけ修正できます",
  companyRequired: "会社名を入れてください",
  issue: "発行",
  issueTitle: "発行前の確認",
  issued: "{deliveryNote} {no} を発行しました。",
  unissue: "取消",
  unissued: "{deliveryNote} {no} を取り消して下書きに戻しました。",
  unissueBilled: "請求書に載っている{deliveryNote}は取り消せません。請求書から外してから取り消してください",
  unissueDisabled: "発行した帳票の取消しは設定で止めています",
  deleteDraft: "下書き削除",
  /** 下書き削除の窓の文は DRAFT_DELETE_MSG（見積書と同じ形。画面の「⋯」と一覧の行の「⋯」） */
  deleted: "{deliveryNote}の下書きを削除しました。",
  rowMenu: "{name}の操作",
  pdfDraft: "PDF で確認（透かし入り）",
  stateMenu: "{deliveryNote}の操作",
  taxOnInvoice: "消費税は請求書にて",
  taxOnLabel: "税を確定する書類",
  taxOnHelp: "請求書：{deliveryNote}に消費税を書かず、請求書で税率ごとに1回計算します（既定）。{deliveryNote}：{deliveryNote}ごとに税率ごとに1回計算して書き、請求書は{deliveryNote}の消費税を足すだけにします。",
  receivedOn: "受領日",
  receivedOnHelp: "{customer}が品物を受け取った日の記録です。紙には印字しません。発行のあとでも入れられます。",
  deliveryDate: "納品日",
  deliveryPlace: "納品先",
  source: "元",
  invoiceOf: "請求書",
  makeInvoice: "請求書作成",
  makeInvoiceTitle: "請求書作成",
  bundleMode: "明細の形",
  bundleHelp: "全行：{deliveryNote}ごとに「日付 納品 番号」の見出しと明細をそのまま写します。1枚1行：{deliveryNote}1枚を1行（日付・番号・税抜の金額）にします。",
  bundleSame: "同じ受注（見積なしは同じ{project}）の発行済・未請求の{deliveryNote}をまとめて1枚の請求書にします",
  bundleMixed: "同じ受注（見積なしは同じ{project}）の{deliveryNote}だけまとめられます",
  bundleTaxMixed: "税を確定する書類の違う{deliveryNote}はまとめられません",
  bundleNotIssued: "発行済・未請求の{deliveryNote}だけ請求書にできます",
  bundleOnInvoice: "{deliveryNote} {no} はもう請求書に載っています",
  invoiceFromNotes: "{deliveryNote}から作成しました（{n}枚）",
  linesLocked: "{deliveryNote}の消費税を足す請求書です。明細は{deliveryNote}で修正してください",
  quoteHasNotes: "この見積には{deliveryNote}があります。請求書は{deliveryNote}から作成します",
  deliveryRequired: "この{customer}は{deliveryNote}で消費税を確定します。請求書は{deliveryNote}から作成します",
  /** 見積の画面（受注）の帯と窓 */
  remaining: "残 {n}行",
  delivered: "納品済",
  dialogTitle: "{deliveryNote}作成",
  colExclude: "外す",
  colQuoteQty: "見積の数量",
  colDelivered: "納品済み",
  colNow: "今回の数量",
  rowsNow: "今回 {n}行",
  totalNow: "合計（税抜）",
  create2: "作成",
  rateRowNote: "率・値引き・別途の行は数量がありません（{deliveryNote}に入れるかを選びます）",
  /** 一覧 */
  empty: "{deliveryNote}はまだありません",
  searchLabel: "{customer}・番号・件名で絞り込み",
  tabsLabel: "{deliveryNote}を状態で絞る",
  colIssueDate: "納品日",
  colNo: "No.",
  colCustomer: "{customer}",
  colSubject: "件名",
  colAmount: "金額（税抜）",
  colState: "状態",
  colInvoice: "請求書",
  selectToBill: "選んだ{deliveryNote}で請求書作成",
  /** ホーム */
  homeCard: "未納品",
  homeRest: "残 合計 {amount} 円",
  homeDelivered: "納品済み・未請求 {n}件",
  /** {project}のタブ */
  groupTab: "{deliveryNote}",
  /** 新しい{deliveryNote}（{project}なし） */
  newCustomer: "{customer}",
  newSubject: "件名",
  newSubmit: "作成",
  /** 設定 */
  revenueBasis: "売上の基準",
  revenueBasisHelp: "今月の実績の売上を、どの日で数えるか。請求日：請求書の請求日。納品日：{deliveryNote}の納品日（{deliveryNote}の無い受注は請求日）。",
} as const;

/** 売上の基準（会社の設定。acceptance＝検収日は検収書（1b）から選べる） */
export const RevenueBasis = z.enum(["invoice", "delivery", "acceptance"]);
export type RevenueBasis = z.infer<typeof RevenueBasis>;
export const REVENUE_BASIS_LABEL: Record<RevenueBasis, string> = { invoice: "請求日", delivery: "納品日", acceptance: "検収日" };
